import React, { useContext } from "react";
import { Catalog, RelationSchema, Row, columnsOf, signatureOf } from "../../powerFlow/language";
import { inputHeader, inputCell } from "./tableStyles";
import KeyIcon from "./KeyIcon";
import ColumnHeader, { HighlightedColumn } from "./ColumnHeader";

const signatureStyle: React.CSSProperties = {
  padding: "5px 8px",
  fontFamily: "monospace",
  fontSize: "11px",
  color: "#666",
  background: "#fbfbf6",
  borderBottom: "1px solid #eee",
};

const descriptionStyle: React.CSSProperties = {
  padding: "8px",
  fontSize: "12px",
  color: "#444",
  background: "#fbfbf6",
  borderBottom: "1px solid #eee",
};

interface Props {
  schema: RelationSchema;
  /** The relation's rows, as buildData produced them. */
  rows?: Row[];
  /**
   * The catalog the relation belongs to — needed to type its columns, since a
   * column that references another relation takes its type from there.
   */
  catalog: Catalog;
}

/**
 * A data relation rendered straight from its schema: key columns first (marked
 * with the key icon), then its value columns, one row per tuple.
 *
 * The relations a rule can name and the tables shown under DATA are the same
 * list, so a relation with no hand-built table of its own — `soc_time`,
 * `gen_cap`, `horizon` — still shows up and can be looked at. Where a richer,
 * editable table exists (generators, lines, batteries), the panel uses that
 * instead.
 */
const RelationTable: React.FC<Props> = ({ schema, rows, catalog }) => {
  const columns = columnsOf(schema);
  // Here the columns *are* the relation's, so a highlighted one can be tinted
  // the whole way down rather than only in its header.
  const highlighted = useContext(HighlightedColumn);
  const data = rows ?? [];

  // A plain-language description (when the schema carries one), then the
  // signature — the key columns and the value they lead to, in the same words a
  // rule that names the relation would get from a hover.
  const header = (
    <>
      {schema.description && <div style={descriptionStyle}>{schema.description}</div>}
      <div style={signatureStyle}>{signatureOf(schema, catalog)}</div>
    </>
  );

  if (data.length === 0) {
    return (
      <div style={{ padding: "12px", color: "#666" }}>
        {header}
        <div style={{ color: "#666", fontStyle: "italic" }}>
          No rows in <code>{schema.name}</code> yet.
        </div>
      </div>
    );
  }

  // Order by the key columns, numbers numerically so timesteps read 0,1,…,10.
  const sorted = [...data].sort((a, b) => {
    for (const key of schema.keys) {
      const [x, y] = [a[key], b[key]];
      if (x === y) continue;
      if (typeof x === "number" && typeof y === "number") return x - y;
      return String(x ?? "").localeCompare(String(y ?? ""));
    }
    return 0;
  });

  const cellText = (value: Row[string]): string => {
    if (value === undefined || value === null) return "";
    // Round the long tails off computed values (gen_cap, demand) without
    // touching ids or whole numbers.
    return typeof value === "number" && !Number.isInteger(value) ? value.toFixed(3) : String(value);
  };

  return (
    <>
      {header}
      <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }}>
        <thead>
          <tr>
            {columns.map((column) => (
              <ColumnHeader key={column} column={column} style={inputHeader}>
                {column}
                {schema.keys.includes(column) && <KeyIcon />}
              </ColumnHeader>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, index) => (
            <tr key={schema.keys.map((key) => row[key]).join("_") || index}>
              {columns.map((column) => (
                <td
                  key={column}
                  style={column === highlighted ? { ...inputCell, background: "#fffbf0" } : inputCell}
                >
                  {cellText(row[column])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default RelationTable;
